import { type FC, useEffect, useMemo, useRef, useState } from 'react'
import { Check, Search, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { useUsers } from '@/hooks/useUsers'
import type { UserListItem } from '@/types/user'

interface UserSearchSelectProps {
    selectedIds: string[]
    onChange: (ids: string[]) => void
    placeholder?: string
    disabled?: boolean
}

/**
 * Search-and-pick control for targeting individual users.
 *
 * Results come from the same `/admin/users` endpoint the Users page uses.
 * Only ids are stored, so a user selected in an earlier session shows as a bare
 * id until a search surfaces their record.
 */
export const UserSearchSelect: FC<UserSearchSelectProps> = ({
    selectedIds,
    onChange,
    placeholder = 'Search by name, email or phone',
    disabled = false,
}) => {
    const [query, setQuery] = useState('')
    const [debouncedQuery, setDebouncedQuery] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)

    /**
     * id -> display label for users picked in this session, so a chip stays
     * readable after the search box is cleared. Recorded on click rather than
     * during render, which keeps it out of the render path.
     */
    const [pickedLabels, setPickedLabels] = useState<Record<string, string>>({})

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query.trim()), 350)
        return () => clearTimeout(timer)
    }, [query])

    // Close the results panel when focus moves elsewhere on the page.
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const { data, isFetching } = useUsers({
        search: debouncedQuery || undefined,
        limit: 10,
    })

    const results: UserListItem[] = useMemo(() => {
        const list = data?.data?.data
        return Array.isArray(list) ? list : []
    }, [data])

    /**
     * A flag loaded for editing carries only ids, so anyone not in the current
     * result set shows as their raw id until a search surfaces them.
     */
    const labelFor = (userId: string) => {
        const inResults = results.find(user => user.userId === userId)
        return (
            pickedLabels[userId] ??
            (inResults
                ? inResults.accountName || inResults.email
                : undefined) ??
            userId
        )
    }

    const toggle = (user: UserListItem) => {
        if (selectedIds.includes(user.userId)) {
            onChange(selectedIds.filter(id => id !== user.userId))
            return
        }

        setPickedLabels(prev => ({
            ...prev,
            [user.userId]: user.accountName || user.email || user.userId,
        }))
        onChange([...selectedIds, user.userId])
    }

    return (
        <div className="space-y-2" ref={containerRef}>
            {selectedIds.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {selectedIds.map(id => (
                        <span
                            key={id}
                            className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-[#E5F5F9] py-1 pl-3 pr-1.5 text-xs text-[#0F172A]"
                        >
                            <span className="truncate">{labelFor(id)}</span>
                            <button
                                type="button"
                                disabled={disabled}
                                className="rounded-full p-0.5 hover:bg-white/70"
                                onClick={() =>
                                    onChange(
                                        selectedIds.filter(
                                            selected => selected !== id
                                        )
                                    )
                                }
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    value={query}
                    disabled={disabled}
                    placeholder={placeholder}
                    className="pl-9"
                    onFocus={() => setIsOpen(true)}
                    onChange={event => {
                        setQuery(event.target.value)
                        setIsOpen(true)
                    }}
                />

                {isOpen && (
                    <div className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-border bg-white shadow-lg">
                        {isFetching && results.length === 0 ? (
                            <p className="px-4 py-5 text-center text-sm text-muted-foreground">
                                Searching...
                            </p>
                        ) : results.length === 0 ? (
                            <p className="px-4 py-5 text-center text-sm text-muted-foreground">
                                No users found
                            </p>
                        ) : (
                            results.map(user => {
                                const selected = selectedIds.includes(
                                    user.userId
                                )

                                return (
                                    <button
                                        key={user.userId}
                                        type="button"
                                        className="flex w-full items-center justify-between gap-3 border-b border-[#F0F0F0] px-4 py-2.5 text-left last:border-b-0 hover:bg-muted/50"
                                        onClick={() => toggle(user)}
                                    >
                                        <span className="min-w-0">
                                            <span className="block truncate text-sm text-[#0F172A]">
                                                {user.accountName || user.email}
                                            </span>
                                            <span className="block truncate text-xs text-muted-foreground">
                                                {user.email}
                                            </span>
                                            <span className="block truncate font-mono text-[11px] text-muted-foreground">
                                                {user.userId}
                                            </span>
                                        </span>
                                        {selected && (
                                            <Check className="h-4 w-4 shrink-0 text-[#17A2B8]" />
                                        )}
                                    </button>
                                )
                            })
                        )}
                    </div>
                )}
            </div>

            <p className="text-xs text-muted-foreground">
                {selectedIds.length} user{selectedIds.length === 1 ? '' : 's'}{' '}
                selected
            </p>
        </div>
    )
}
